import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { ChatBubble, ExternalLinkWarningModal, InteractiveChatBubble } from '../..';
import type { Brand } from '../../types/brand';
import type { ExternalLinkWarningData } from '../modals/ExternalLinkWarningModal';
import { isInternalUrl } from '../../utils/urlUtils';

const EXTERNAL_LINK_MODAL_QUERY_PARAM = 'externalLinkWarningModalIsOpened';

export type ChatBubbleWithLinkFeatureToggleProps = {
  children: React.ReactNode;
  senderName?: string;
  shouldIncludeGutter?: boolean;
  variant: 'autoResponse' | 'messageOwner' | 'default';
  enableClickableLinks?: boolean;
  enableExternalLinkWarningModal?: boolean;
  externalLinkWarningData?: ExternalLinkWarningData;
  brand?: Brand;
  onImpact?: boolean;
  onExternalLinkWarningModalOpen?: () => void;
  onExternalLinkClickInModal?: () => void;
  /**
   * When enabled, syncs modal state with URL via query parameters.
   * This allows the browser back button to close the modal and return to the previous page
   * instead of navigating away from the inbox.
   * @default false
   */
  enableHistoryIntegration?: boolean;
};

/**
 * ChatBubbleWithLinkFeatureToggle - Support feature flag for ChatBubble with clickable links
 *
 * This component allows satellite-rachel-web and satellite-landlord-web to
 * safely roll out clickable link functionality via feature flags while
 * maintaining the ability to fallback to the original ChatBubble behavior.
 *
 * When enableExternalLinkWarningModal is true, external links will show a warning modal
 * before navigating to the external site.
 */
export default function ChatBubbleWithLinkFeatureToggle({
  children,
  senderName,
  shouldIncludeGutter = false,
  variant,
  enableClickableLinks = false,
  enableExternalLinkWarningModal = false,
  externalLinkWarningData,
  brand = 'zillow',
  onImpact,
  onExternalLinkWarningModalOpen,
  onExternalLinkClickInModal,
  enableHistoryIntegration = false,
}: ChatBubbleWithLinkFeatureToggleProps) {
  const [modalState, setModalState] = useState<{ isOpen: boolean; url: string; displayText: string }>({
    isOpen: false,
    url: '',
    displayText: '',
  });

  // Check if query parameter exists in current URL
  const hasModalQueryParam = useCallback(() => {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    return params.has(EXTERNAL_LINK_MODAL_QUERY_PARAM);
  }, []);

  // Add query parameter to URL
  const addModalQueryParam = useCallback(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    params.set(EXTERNAL_LINK_MODAL_QUERY_PARAM, 'true');
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState(null, '', newUrl);
  }, []);

  // Remove query parameter from URL (without navigation)
  const removeModalQueryParam = useCallback(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    params.delete(EXTERNAL_LINK_MODAL_QUERY_PARAM);
    const newUrl = params.toString() ? `${window.location.pathname}?${params.toString()}` : window.location.pathname;
    window.history.replaceState(null, '', newUrl);
  }, []);

  // Sync modal state with URL query parameter for browser history integration
  useEffect(() => {
    if (!enableHistoryIntegration) {
      return;
    }

    const handlePopState = () => {
      const hasParam = hasModalQueryParam();

      if (modalState.isOpen && !hasParam) {
        // Back button: close modal when query param is removed
        setModalState({ isOpen: false, url: '', displayText: '' });
      } else if (!modalState.isOpen && hasParam) {
        // Forward button: clean up stale query param (can't restore modal state)
        removeModalQueryParam();
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [enableHistoryIntegration, modalState.isOpen, hasModalQueryParam, removeModalQueryParam]);

  const handleLinkClick = (url: string, displayText?: string) => {
    const isInternal = isInternalUrl(url, externalLinkWarningData?.allowListedDomains);

    if (enableExternalLinkWarningModal && !isInternal) {
      // Add query parameter to sync modal state with URL
      if (enableHistoryIntegration) {
        addModalQueryParam();
      }

      // Show modal for external URLs
      setModalState({ isOpen: true, url, displayText: displayText || url });
      onExternalLinkWarningModalOpen?.();
    } else {
      // Default behavior: open link directly (internal URLs or modal disabled)
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleModalClose = () => {
    setModalState({ isOpen: false, url: '', displayText: '' });

    // Navigate back in history to remove the query parameter naturally
    if (enableHistoryIntegration && hasModalQueryParam()) {
      window.history.back();
    }
  };

  if (enableClickableLinks) {
    return (
      <>
        <InteractiveChatBubble
          senderName={senderName}
          shouldIncludeGutter={shouldIncludeGutter}
          variant={variant}
          onImpact={onImpact}
          onLinkClick={enableExternalLinkWarningModal ? handleLinkClick : undefined}
        >
          {children}
        </InteractiveChatBubble>

        {enableExternalLinkWarningModal && (
          <ExternalLinkWarningModal
            brand={brand}
            isOpen={modalState.isOpen}
            url={modalState.url}
            displayText={modalState.displayText}
            warningData={externalLinkWarningData}
            onClose={handleModalClose}
            onExternalLinkClick={onExternalLinkClickInModal}
          />
        )}
      </>
    );
  }

  return (
    <ChatBubble senderName={senderName} shouldIncludeGutter={shouldIncludeGutter} variant={variant}>
      {children}
    </ChatBubble>
  );
}
